import { useEffect, useMemo, useState } from "react";
import { patchReplace } from "../../../helpers/patchDoc";
import { useCreateClientVulnerabilityMutation, useDeleteClientVulnerabilityMutation, useFetchVulnerabilityCategoriesQuery, useFetchVulnerabilityTypesQuery, useLazyFetchClientVulnerabilitiesQuery, usePatchClientVulnerabilityMutation, useUpdateVulnerableAccountStatusMutation } from "../../../services/clientfactfind";
import { useClientContext } from "../../../hooks/ClientContext";

const useClientVulnerabilities = (vulnerabilitySectionId, clientVulnerabilityIds) => {
    const { id: masterAccountId } = useClientContext();
    const [fetchClientVulnerabilities, { data, isLoading, isFetching, isUninitialized, isError, error }] = useLazyFetchClientVulnerabilitiesQuery();
    const [create] = useCreateClientVulnerabilityMutation();
    const [patch] = usePatchClientVulnerabilityMutation();
    const [remove] = useDeleteClientVulnerabilityMutation();
    const [updateVulnerabilityStatus] = useUpdateVulnerableAccountStatusMutation();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { data: vulnerabilityCategories = [], isLoading: vulnerabilityCategoriesLoading, isError: vulnerabilityCategoriesError } = useFetchVulnerabilityCategoriesQuery();
    const { data: vulnerabilityTypeByCategory = [], isLoading: vulnerabilityTypesLoading, isError: vulnerabilityTypesError } = useFetchVulnerabilityTypesQuery();

    useEffect(() => {
        if (vulnerabilitySectionId != null)
            fetchClientVulnerabilities({ vulnerabilitySectionId, masterAccountId });
    }, [vulnerabilitySectionId, fetchClientVulnerabilities])

    const createClientVulnerability = () => {
        return new Promise((resolve, reject) => {
            return create({ vulnerabilitySectionId, masterAccountId })
                .unwrap()
                .then(
                    r => resolve(r),
                    e => reject(e)
                );
        });
    };

    const patchClientVulnerability = (clientVulnerabilityId, property, newValue) => {
        return new Promise((resolve, reject) => {
            const operations = [patchReplace(property, newValue)];
            return patch({ clientVulnerabilityId, operations, vulnerabilitySectionId, masterAccountId: masterAccountId })
                .unwrap()
                .then(
                    r => resolve(r),
                    e => reject(e)
                );
        });
    };

    const removeClientVulnerability = (clientVulnerabilityId) => {
        return new Promise((resolve, reject) => {
            return remove({ clientVulnerabilityId, vulnerabilitySectionId, masterAccountId })
                .unwrap()
                .then(
                    r => resolve(r),
                    e => reject(e)
                );
        });
    };

    const updateVulnerableAccountStatus = (vulnerableStatus) => {
        return new Promise((resolve, reject) => {
            return updateVulnerabilityStatus({ masterAccountId, vulnerableStatus })
                .unwrap()
                .then(
                    r => resolve(r),
                    e => reject(e)
                );
        });
    };

    const loadingData = useMemo(() => (isLoading || isFetching || isUninitialized)
        ? clientVulnerabilityIds?.map(clientVulnerabilityId => ({ clientVulnerabilityId }))
        : data,
        [data, clientVulnerabilityIds, isFetching, isLoading, isUninitialized])

    return {
        data: loadingData || [],
        isLoading: isLoading || isFetching || isUninitialized,
        isError,
        error,
        refetchClientVulnerabilities: () => fetchClientVulnerabilities({ vulnerabilitySectionId, masterAccountId }),
        createClientVulnerability,
        patchClientVulnerability,
        removeClientVulnerability,
        updateVulnerableAccountStatus,
        vulnerabilityCategories,
        vulnerabilityCategoriesLoading,
        vulnerabilityCategoriesError,
        vulnerabilityTypeByCategory,
        vulnerabilityTypesLoading,
        vulnerabilityTypesError,
        selectedCategory,
        setSelectedCategory,
        masterAccountId
    };
};

export default useClientVulnerabilities;